import { InsightsOutlined } from '@mui/icons-material';
import WidgetCard from 'switch-fe-shared/components/Cards/WidgetCard';
import type { PpaSiteCumulative } from 'switch-shared/modules/dashboard/ppa-site.dto';
import { formatNumber } from 'switch-shared/utils/formatters';

import {
  formatPercent,
  monthKeyLabel,
  performanceRatio,
} from '../../utils/solarPresentation';
import SolarCardState, { type SolarCardStatus } from './SolarCardState';
import SolarPerformanceValue from './SolarPerformanceValue';
import SolarSummaryTable from './SolarSummaryTable';

interface KpiRow {
  label: string;

  actual: string;

  forecast: string;

  /** Actual over forecast, or null without a forecast. */
  ratio: number | null;
}

interface Props {
  status: SolarCardStatus;

  cumulative?: PpaSiteCumulative;

  hasForecast: boolean;

  /** Whether the site has a municipal meter point, without which its load is unknown. */
  hasMunicipal: boolean;
}

/** Solar energy, demand reduction, solar share of load and carbon avoided since project start, against forecast, over the months with solar data. */
const SolarProjectKpiCard = ({
  status,
  cumulative,
  hasForecast,
  hasMunicipal,
}: Props) => {
  const rows: KpiRow[] = cumulative
    ? [
        {
          label: 'Solar energy (kWh)',
          actual: formatNumber(cumulative.solarKwh, 0),
          forecast: hasForecast ? formatNumber(cumulative.forecastKwh, 0) : '—',
          ratio: hasForecast
            ? performanceRatio(cumulative.solarKwh, cumulative.forecastKwh)
            : null,
        },
        {
          label: 'Demand reduction (kVA-months)',
          actual: formatNumber(cumulative.demandReductionKvaMonths, 0),
          forecast: hasForecast
            ? formatNumber(cumulative.forecastDemandReductionKvaMonths, 0)
            : '—',
          ratio: hasForecast
            ? performanceRatio(
                cumulative.demandReductionKvaMonths,
                cumulative.forecastDemandReductionKvaMonths
              )
            : null,
        },
        {
          label: 'Solar share of site load',
          actual: (() => {
            const share = hasMunicipal
              ? performanceRatio(
                  cumulative.selfConsumedKwh,
                  cumulative.siteLoadKwh
                )
              : null;
            return share == null ? '—' : formatPercent(share);
          })(),
          forecast: '—',
          ratio: null,
        },
        {
          label: 'CO₂e avoided (t)',
          actual: formatNumber(cumulative.co2AvoidedKg / 1000, 1),
          forecast: '—',
          ratio: null,
        },
      ]
    : [];

  return (
    <WidgetCard
      title='Since Project Start'
      subheader={
        cumulative
          ? `${monthKeyLabel(cumulative.fromKey)} to ${monthKeyLabel(cumulative.toKey)}`
          : undefined
      }
      avatar={<InsightsOutlined />}
      cardContentProps={{
        sx: { display: 'flex', flexDirection: 'column', pb: '16px !important' },
      }}
    >
      <SolarCardState status={status}>
        <SolarSummaryTable
          columns={[
            {
              key: 'label',
              header: 'Measure',
              render: (row: KpiRow) => row.label,
            },
            {
              key: 'actual',
              header: 'Actual',
              align: 'right',
              render: (row) => row.actual,
            },
            {
              key: 'forecast',
              header: 'Forecast',
              align: 'right',
              render: (row) => row.forecast,
            },
            {
              key: 'performance',
              header: 'vs Forecast',
              align: 'right',
              render: (row) =>
                row.ratio == null ? (
                  '—'
                ) : (
                  <SolarPerformanceValue ratio={row.ratio} />
                ),
            },
          ]}
          rows={rows}
          getRowKey={(row) => row.label}
          caption={
            cumulative?.withoutSolarData.length
              ? `${cumulative.withoutSolarData.map(monthKeyLabel).join(', ')} ${cumulative.withoutSolarData.length === 1 ? 'has' : 'have'} no solar meter data and ${cumulative.withoutSolarData.length === 1 ? 'is' : 'are'} left out.`
              : undefined
          }
        />
      </SolarCardState>
    </WidgetCard>
  );
};

export default SolarProjectKpiCard;
